
import { useState } from "react";
import { Link } from "react-router-dom";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import { useMaterialUIController } from "context";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import axios from "axios";
import { useForm } from "react-hook-form";
import { useNavigate } from 'react-router-dom';
import configurations from "config";
import { useTranslation } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import 'assets/css/common.css';
import Backdrop from '@mui/material/Backdrop';

function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { MyContext } = controller;
  const [isLoading, setIsLoading] = useState(false);
  const baseUrl = configurations.baseUrl;
  const azureFunctionVendorFrontendKey = configurations.azureFunctionVendorFrontendKey;
  const [toastClosed, setToastClosed] = useState(false);

  const { register, handleSubmit, watch, formState: { errors }, setError } = useForm();

  const handleToastClose = () => {
    navigate('/vendor/login');
    setToastClosed(true);
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    await axios.post(baseUrl + '/vendor/register', data,
      {
        headers: { function: azureFunctionVendorFrontendKey }
      }).then((response) => {
        setIsLoading(false);
        if (response.status == 200) {
          const status = response.data.status;
          const message = response.data.message;
          if (status == 1) {
            toast.success(t(message),{ autoClose: 10000, onClose: handleToastClose });
            const redirectTimeout = setTimeout(() => {
              clearTimeout(redirectTimeout);
              if (!toastClosed) {
                handleToastClose();
              }
            }, 10000);
          }
          else {
            if (message != '')
              toast.error(t(message));
            if (response && response.data && response.data.errors) {
              const errorData = response.data.errors;
              errorData.forEach((error) => {
                setError(error.field, {
                  type: 'server',
                  message: t(error.errors[0])
                });
              });
            }
          }
        }
        else {
          toast.error(t('unableToProccessRequest'));
        }
      }).catch(error => {
        setIsLoading(false);
        toast.error(error);
      });
  }

  return (
    <BasicLayout image={bgImage}>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}><CircularProgress color="inherit" /></Backdrop>
      <Card>
        <MDBox variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="success" mx={2} mt={-3} p={3} mb={1} textAlign="center">
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>{t('joinUsToday')}</MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit(onSubmit)}>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label={t('name')}
                name="name"
                {...register("name", {
                  required: {
                    value: true,
                    message: t('nameRequired')
                  },
                })}
                onChange={((e) => { e.stopPropagation() })}
                fullWidth />
              {errors.name && (<p className="errorMsg">{errors.name.message}</p>)}
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label={t('email')}
                name="email"
                {...register("email", {
                  required: {
                    value: true,
                    message: t('emailRequired')
                  },
                  pattern: {
                    value: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/,
                    message: t('invalidEmail')
                  }
                })}
                onChange={((e) => { e.stopPropagation() })}
                fullWidth />
              {errors.email && (<p className="errorMsg">{errors.email.message}</p>)}
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label={t('password')}
                name="password"
                {...register("password", {
                  required: {
                    value: true,
                    message: t('passwordRequired')
                  },
                  minLength: {
                    value: MyContext.minPasswordLength,
                    message: t('passwordMinLength', { minPasswordLength: MyContext.minPasswordLength })
                  }
                })}
                onChange={((e) => { e.stopPropagation() })}
                fullWidth />
              {errors.password && (<p className="errorMsg">{errors.password.message}</p>)}
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
               label={t('confirmPassword')}
                name="confirmPassword"
                {...register("confirmPassword", {
                  required: {
                    value: true,
                    message: t('passwordRequired')
                  },
                  minLength: {
                    value: MyContext.minPasswordLength,
                    message: t('passwordMinLength', { minPasswordLength: MyContext.minPasswordLength })
                  },
                  validate: (value) => {
                    if (watch('password') != value) {
                      return t('passwordMismatch');
                    }
                  },
                })}
                onChange={((e) => { e.stopPropagation() })}
                fullWidth />
              {errors.confirmPassword && (<p className="errorMsg">{errors.confirmPassword.message}</p>)}
            </MDBox>
            <MDBox mb={2} >
              <MDBox mb={2} display="flex">
                <MDInput
                  type="checkbox"
                  name="acceptTerms"
                  variant="standard"
                  {...register("acceptTerms", {
                    required: {
                      value: true,
                      message: t('acceptTermsAndConditions')
                    },
                  })}
                />
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  sx={{ cursor: "pointer", userSelect: "none", ml: 0 }}
                >
                  &nbsp;&nbsp;{t('iAgree')}&nbsp;
                </MDTypography>
                <MDTypography
                  component={Link}
                  to="/vendor/acceptTermsAndConditions"
                  variant="button"
                  fontWeight="bold"
                  color="info"
                  textGradient
                  target="_blank"
                >
                  {t('termsAndConditions')}
                </MDTypography>
              </MDBox>
              {errors.acceptTerms && (<p className="errorMsg">{errors.acceptTerms.message}</p>)}
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton type="submit" variant="gradient" color="info" disableRipple fullWidth>
                {t('signUp')}
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                {t('alreadyHaveAnAccount')}
                <MDTypography
                  component={Link}
                  to="/vendor/login"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  {t('signIn')}
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Register;
